import { Directive, ElementRef, HostListener, NgModule } from '@angular/core';

@Directive({
  selector: '[appNoWhiteSpace]',
})
export class NoWhiteSpaceInputDirective {
  // Regex to allow only alphanumeric characters, dash (-), and slash (/)
  private readonly regex: RegExp = /^[a-zA-Z0-9\/-]*$/;
  private readonly specialKeys: Set<string> = new Set([
    'Backspace',
    'Tab',
    'End',
    'Home',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Delete',
    'Control',
    'Meta',
    'Shift',
  ]);

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const inputElement = this.el.nativeElement;
    const originalValue = inputElement.value;

    // Remove all whitespace characters
    const cleanedValue = originalValue.replace(/\s+/g, '');

    // Only update if value changed to avoid unnecessary re-renders
    if (originalValue !== cleanedValue) {
      inputElement.value = cleanedValue;
      // Trigger input event for reactive forms/change detection
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const inputElement = this.el.nativeElement;

    // Allow special keys to work normally (including Ctrl+C, Ctrl+V, etc.)
    if (this.specialKeys.has(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    // Prevent space key explicitly
    if (event.key === ' ') {
      event.preventDefault();
      return;
    }

    // Check if the character would create invalid input
    const current: string = inputElement.value;
    const position = inputElement.selectionStart ?? 0;
    const next: string = [
      current.slice(0, position),
      event.key,
      current.slice(position),
    ].join('');

    // Prevent invalid characters
    if (!this.regex.test(next)) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    // Remove spaces from pasted text
    const cleanedText = pastedText.replace(/\s+/g, '');

    // Validate the cleaned text
    if (this.regex.test(cleanedText)) {
      const inputElement = this.el.nativeElement;
      const current = inputElement.value;
      const position = inputElement.selectionStart ?? 0;

      // Insert cleaned text at cursor position
      const newValue = [
        current.slice(0, position),
        cleanedText,
        current.slice(position),
      ].join('');

      if (this.regex.test(newValue)) {
        inputElement.value = newValue;
        inputElement.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  }
}

@NgModule({
  declarations: [NoWhiteSpaceInputDirective],
  exports: [NoWhiteSpaceInputDirective],
})
export class NoWhiteSpaceInputDirectiveModule {}
